import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useGuest } from '../context/GuestContext';
import '../styles/CoupleSection.css';

const CHECKLISTS = {
  amiker: {
    title: "Bride's Checklist",
    items: [
      'Choose the perfect dress',
      'Finalise the guest list',
      'Book hair & makeup',
      'Pick flowers & bouquet',
      'Write your vows',
      'Plan the seating chart (good luck)',
      'Organise bridesmaids',
      'Final dress fitting',
      'Pack an emergency kit',
      'Try not to cry during the ceremony',
    ],
  },
  gergely: {
    title: "Groom's Checklist",
    items: [
      'Get the suit sorted',
      'Buy the rings (DO NOT LOSE THEM)',
      'Plan the honeymoon',
      'Write your vows (not the night before)',
      'Organise groomsmen',
      'Book the music / DJ',
      'Prepare your speech',
      'Get a haircut (not too short)',
      'Learn at least one dance move',
      'Show up on time',
    ],
  },
};

const CoupleSection = () => {
  const { guest } = useGuest();
  const [checked, setChecked] = useState(() => {
    try {
      const saved = localStorage.getItem('coupleChecklist');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  if (guest?.special !== 'amiker' && guest?.special !== 'gergely') {
    return null;
  }

  const checklist = CHECKLISTS[guest.special];
  const partnerName = guest.special === 'amiker' ? 'Gergely' : 'Amiker';
  const isBride = guest.special === 'amiker';

  const toggleItem = (index) => {
    const next = { ...checked, [index]: !checked[index] };
    setChecked(next);
    localStorage.setItem('coupleChecklist', JSON.stringify(next));
  };

  const completedCount = Object.values(checked).filter(Boolean).length;

  return (
    <section className="couple-section">
      {/* Checklist */}
      <motion.div
        className="couple-checklist"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="couple-section-title">{checklist.title}</h2>
        <p className="couple-progress">
          {completedCount} / {checklist.items.length} done
        </p>
        <div className="checklist-items">
          {checklist.items.map((item, i) => (
            <motion.label
              key={i}
              className={`checklist-item ${checked[i] ? 'checked' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => toggleItem(i)}
              />
              <span className="checkmark" />
              <span className="checklist-text">{item}</span>
            </motion.label>
          ))}
        </div>
      </motion.div>

      {/* Support section */}
      <motion.div
        className="couple-support"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="support-card">
          <div className={`support-accent ${isBride ? 'accent-bride' : 'accent-groom'}`} />
          <h3 className="support-title">
            {isBride ? "Your Groom Has Your Back" : "Your Bride Has Your Back"}
          </h3>
          <p className="support-text">
            {isBride
              ? `Whatever happens on the big day, ${partnerName} is right there with you. Through every stressful seating chart, every late-night decision, every "does this look okay?" — he's your biggest fan and your teammate. Always.`
              : `Whatever happens on the big day, ${partnerName} is right there with you. Through every forgotten detail, every last-minute panic, every "I don't know what to wear" — she's your anchor and your best friend. Always.`
            }
          </p>
          <div className="support-divider" />
          <p className="support-quote">
            {isBride
              ? '"I can\'t wait to see you walk down that aisle." — G'
              : '"I can\'t wait to see you waiting at the end of that aisle." — A'
            }
          </p>
        </div>
      </motion.div>
    </section>
  );
};

export default CoupleSection;
